/**
 * Binds DOM event listeners and emits decoded GameIntents.
 * Owns zero game state — purely translates raw DOM events to semantic intents.
 */
import type { GameIntent } from './inputTypes';

export type { GameIntent };
export type { PaintStartIntent, PaintMoveIntent, PaintEndIntent, HoverIntent, HoverLeaveIntent, RotateStartIntent, RotateStopIntent, SpeedStartIntent, SpeedStopIntent, ZoomStartIntent, ZoomStopIntent, TiltStartIntent, TiltStopIntent, SnapDownIntent, SnapUpIntent, ToggleFullframeIntent, TogglePlaneIntent, BrushSizeDeltaIntent, ResizeIntent } from './inputTypes';

export type TileResolver = (event: PointerEvent) => { x: number; y: number } | null;
export type IntentListener = (intent: GameIntent) => void;

export interface InputBinding {
  /** Removes all DOM listeners. */
  destroy(): void;
}

/**
 * Attaches keyboard, pointer, wheel, contextmenu, and resize listeners.
 * Calls `onIntent` for each decoded semantic intent.
 * Returns an object with `destroy()` to remove all listeners.
 */
export function bindInput(
  elements: {
    board: HTMLCanvasElement;
    boardWrap: HTMLElement;
    window: Window;
  },
  tileResolver: TileResolver,
  onIntent: IntentListener,
): InputBinding {
  const { board, boardWrap, window: win } = elements;

  // --- Contextmenu ---
  const onContextMenu = (e: Event) => e.preventDefault();
  board.addEventListener('contextmenu', onContextMenu);

  // --- Pointer: board ---
  const onPointerDown = (event: PointerEvent) => {
    let brushOverride: 'lift' | 'lower' | undefined;
    if (event.pointerType === 'mouse') {
      if (event.button === 0) {
        brushOverride = 'lower';
      } else if (event.button === 2) {
        brushOverride = 'lift';
      } else {
        return;
      }
    }
    const tile = tileResolver(event);
    onIntent({ type: 'paint.start', tile, brushOverride });
  };

  const onPointerMove = (event: PointerEvent) => {
    const tile = tileResolver(event);
    onIntent({ type: 'hover', tile });
    onIntent({ type: 'paint.move', tile });
  };

  const onPointerLeave = () => {
    onIntent({ type: 'hover.leave' });
  };

  const onPointerUp = () => {
    onIntent({ type: 'paint.end' });
  };

  board.addEventListener('pointerdown', onPointerDown);
  board.addEventListener('pointermove', onPointerMove);
  board.addEventListener('pointerleave', onPointerLeave);
  win.addEventListener('pointerup', onPointerUp);

  // --- Keyboard ---
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.repeat) return;
    const key = event.key;
    if (key === 'a' || key === 'A' || key === 'ArrowLeft') {
      onIntent({ type: 'rotate.start', dir: 'ccw' });
    } else if (key === 'd' || key === 'D' || key === 'ArrowRight') {
      onIntent({ type: 'rotate.start', dir: 'cw' });
    } else if (key === 'x' || key === 'X') {
      onIntent({ type: 'snap.down' });
    } else if (key === 'f' || key === 'F') {
      onIntent({ type: 'toggle.fullframe' });
    } else if (key === 'p' || key === 'P') {
      onIntent({ type: 'toggle.plane' });
    } else if (key === '1') {
      onIntent({ type: 'speed.start', dir: 'down' });
    } else if (key === '2') {
      onIntent({ type: 'speed.start', dir: 'up' });
    } else if (key === 'q' || key === 'Q') {
      onIntent({ type: 'zoom.start', dir: 'out' });
    } else if (key === 'e' || key === 'E') {
      onIntent({ type: 'zoom.start', dir: 'in' });
    } else if (key === 's' || key === 'S' || key === 'ArrowDown') {
      onIntent({ type: 'tilt.start', dir: 'down' });
    } else if (key === 'w' || key === 'W' || key === 'ArrowUp') {
      onIntent({ type: 'tilt.start', dir: 'up' });
    }
  };

  const onKeyUp = (event: KeyboardEvent) => {
    const key = event.key;
    if (key === 'x' || key === 'X') {
      onIntent({ type: 'snap.up' });
    } else if (key === 'a' || key === 'A' || key === 'ArrowLeft') {
      onIntent({ type: 'rotate.stop', dir: 'ccw' });
    } else if (key === 'd' || key === 'D' || key === 'ArrowRight') {
      onIntent({ type: 'rotate.stop', dir: 'cw' });
    } else if (key === '1') {
      onIntent({ type: 'speed.stop', dir: 'down' });
    } else if (key === '2') {
      onIntent({ type: 'speed.stop', dir: 'up' });
    } else if (key === 'q' || key === 'Q') {
      onIntent({ type: 'zoom.stop', dir: 'out' });
    } else if (key === 'e' || key === 'E') {
      onIntent({ type: 'zoom.stop', dir: 'in' });
    } else if (key === 's' || key === 'S' || key === 'ArrowDown') {
      onIntent({ type: 'tilt.stop', dir: 'down' });
    } else if (key === 'w' || key === 'W' || key === 'ArrowUp') {
      onIntent({ type: 'tilt.stop', dir: 'up' });
    }
  };

  win.addEventListener('keydown', onKeyDown);
  win.addEventListener('keyup', onKeyUp);

  // --- Wheel ---
  const onWheel = (event: WheelEvent) => {
    event.preventDefault();
    const delta = event.deltaY < 0 ? 0.2 : -0.2;
    onIntent({ type: 'brush.sizeDelta', delta });
  };
  board.addEventListener('wheel', onWheel, { passive: false });

  // --- Resize ---
  const onResize = () => onIntent({ type: 'viewport.resize' });
  const resizeObserver =
    typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : null;
  resizeObserver?.observe(boardWrap);
  win.addEventListener('resize', onResize);

  // --- Destroy ---
  return {
    destroy() {
      board.removeEventListener('contextmenu', onContextMenu);
      board.removeEventListener('pointerdown', onPointerDown);
      board.removeEventListener('pointermove', onPointerMove);
      board.removeEventListener('pointerleave', onPointerLeave);
      win.removeEventListener('pointerup', onPointerUp);
      win.removeEventListener('keydown', onKeyDown);
      win.removeEventListener('keyup', onKeyUp);
      board.removeEventListener('wheel', onWheel);
      resizeObserver?.disconnect();
      win.removeEventListener('resize', onResize);
    },
  };
}
